import { canPromoteInPlace } from "../../trading-accounts/fundedLineage";
import { useNavigate } from "react-router-dom";
import { Link, routes } from "wasp/client/router";
import { Wallet, Plus, AlertCircle, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "../../client/components/ui/card";
import { CardSectionHeader } from "../../client/components/ui/card-section-header";
import { Button } from "../../client/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../client/components/ui/tooltip";
import { useDashboardData } from "./DashboardDataContext";
import BalanceGapHint from "../../trading-accounts/BalanceGapHint";
import { buildViolationDetails } from "../../trading-accounts/violationLabels";
import { formatViolationLabel } from "../../trading-accounts/formatViolationLabel";

export default function AccountsTableWidget() {
  const navigate = useNavigate();
  const {
    accounts,
    t,
    isDark,
    readOnly,
    onPromoteToFunded,
  } = useDashboardData();

  return (
    <Card className="h-full flex flex-col">
      <CardSectionHeader
        label={t.dashboard.accountsLabel}
        isDark={isDark}
        rightContent={
          <Link to={routes.TradingAccountsRoute.to}>
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs">{t.dashboard.viewAll}</Button>
          </Link>
        }
      />
      <CardContent className="px-0 pb-0 pt-0 flex-1 overflow-y-auto min-h-0">
        {(accounts || []).filter((a: any) => a.isActive !== false).length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Wallet className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">{t.dashboard.noAccounts}</p>
            <Link to={routes.TradingAccountsRoute.to}>
              <Button size="sm" variant="default" className="text-xs">
                <Plus className="h-3.5 w-3.5 mr-1" />
                {t.dashboard.addAccount}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60">
                  <th className="px-5 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableName}</th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableFirm}</th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableStatus}</th>
                  <th className="px-4 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableStrategy}</th>
                  <th className="px-4 py-3 text-right text-[10px] font-semibold tracking-widest uppercase text-muted-foreground">{t.dashboard.accountsTableBalance}</th>
                  <th className="px-5 py-3 text-left text-[10px] font-semibold tracking-widest uppercase text-muted-foreground min-w-[160px]">{t.dashboard.accountsTableObjective}</th>
                </tr>
              </thead>
              <tbody>
                {((accounts || []) as any[])
                  .filter((a: any) => a.isActive !== false)
                  .map((account: any, idx: number, arr: any[]) => {
                    const balance = account.currentBalance ?? account.initialBalance;
                    const isChallenge = account.status === "challenge";
                    const challenge = account.challengeProgress;
                    const template = account.effectiveTemplate;
                    const isLast = idx === arr.length - 1;

                    const violations = account.ruleViolations as Record<string, any> | undefined;
                    const violationEntries = violations ? Object.values(violations) : [];
                    const hasViolation = violationEntries.some((v: any) => v.types?.some((tp: string) => tp.endsWith("_violation")));
                    const hasWarning = !hasViolation && violationEntries.some((v: any) => v.types?.some((tp: string) => tp.endsWith("_warning")));
                    // Libellés CHIFFRÉS : « 34% le 2026-09-24 (seuil 30%) » plutôt
                    // que « Règle de consistance violée » seul, qui ne permettait
                    // ni de vérifier la règle ni de distinguer un vrai
                    // franchissement d'un bug d'affichage.
                    const violationLabels = buildViolationDetails(violationEntries as any)
                      .map((d) =>
                        formatViolationLabel(d, {
                          dailyLossViolation: t.dashboard.calendar.violations.dailyLossViolation,
                          dailyLossWarning: t.dashboard.calendar.violations.dailyLossWarning,
                          consistencyViolation: t.dashboard.calendar.violations.consistencyViolation,
                          consistencyWarning: t.dashboard.calendar.violations.consistencyWarning,
                          consistencyViolationDetail: t.dashboard.calendar.violations.consistencyViolationDetail,
                          consistencyWarningDetail: t.dashboard.calendar.violations.consistencyWarningDetail,
                        }),
                      )
                      .filter((v: string, i: number, a: string[]) => a.indexOf(v) === i);

                    const statusColors: Record<string, { dot: string; text: string; bg: string }> = {
                      challenge: { dot: "bg-amber-400", text: "text-amber-500", bg: "bg-amber-400/10" },
                      pa:        { dot: "bg-success", text: "text-success", bg: "bg-success/10" },
                      live:      { dot: "bg-success", text: "text-success", bg: "bg-success/10" },
                      demo:      { dot: "bg-blue-400", text: "text-blue-400", bg: "bg-blue-400/10" },
                      inactive:  { dot: "bg-muted-foreground/40", text: "text-muted-foreground", bg: "bg-muted/40" },
                    };
                    const sc = statusColors[account.status] ?? statusColors.inactive;

                    const statusLabel: Record<string, string> = {
                      challenge: t.tradingAccounts.statusChallenge,
                      pa: t.dashboard.statusFunded,
                      live: t.tradingAccounts.statusLive,
                      demo: t.tradingAccounts.statusDemo,
                      inactive: t.tradingAccounts.statusInactive,
                    };

                    const challengeRatio = challenge ? Math.min(1, challenge.ratio) : 0;
                    const hasObjectiveBar = isChallenge && challenge && (challenge.targetPercent !== null || challenge.targetAmount !== null);

                    return (
                      <tr
                        key={account.id}
                        className={`group cursor-pointer hover:bg-muted/30 transition-colors ${!isLast ? "border-b border-border/40" : ""}`}
                        onClick={() => navigate(`/account/${account.id}`)}
                      >
                        {/* Name */}
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-2">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${sc.dot}`} />
                            <span className="font-semibold text-sm truncate max-w-[140px]">{account.name}</span>
                            {(hasViolation || hasWarning) && (
                              <TooltipProvider delayDuration={200}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="shrink-0 cursor-default">
                                      {hasViolation
                                        ? <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                                        : <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />
                                      }
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="max-w-[220px]">
                                    <ul className="space-y-0.5">
                                      {violationLabels.map((label: string, i: number) => <li key={i}>{label}</li>)}
                                    </ul>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                        </td>

                        {/* Prop Firm + Tier */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {template ? (
                              <span
                                className="text-[10px] font-semibold px-2 py-0.5 rounded-full leading-none"
                                style={{ backgroundColor: template.color, color: template.textColor }}
                              >
                                {template.name}
                              </span>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                            {account.activeTier && (
                              <span className="text-[10px] text-muted-foreground font-mono bg-muted px-1.5 py-0.5 rounded-sm leading-none">
                                {account.activeTier.label}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3.5">
                          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${sc.text} ${sc.bg}`}>
                            {statusLabel[account.status] ?? account.status}
                          </span>
                        </td>

                        {/* Strategy */}
                        <td className="px-4 py-3.5">
                          {account.strategies && account.strategies.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {account.strategies.map((s: any) => (
                                <span
                                  key={s.id}
                                  className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground"
                                >
                                  <span
                                    className="w-1.5 h-1.5 rounded-full shrink-0"
                                    style={{ backgroundColor: s.color }}
                                  />
                                  {s.name}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>

                        {/* Balance */}
                        <td className="px-4 py-3.5 text-right">
                          <div className="flex flex-col items-end gap-0.5">
                            <span className="font-mono font-bold text-sm">
                              {balance.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                            </span>
                            {/* Ce solde vient du broker : quand il est en retard
                                sur les trades, l'objectif calculé dessus affiche
                                +0 alors que le calendrier montre du PnL. Le dire
                                ici évite deux écrans qui se contredisent. */}
                            <BalanceGapHint
                              reconciliation={(account as any).balanceReconciliation}
                              formatCurrency={(v) =>
                                v.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })
                              }
                            />
                          </div>
                        </td>

                        {/* Objective progress bar */}
                        <td className="px-5 py-3.5 min-w-[160px]">
                          {hasObjectiveBar ? (
                            <div>
                              <div className="flex justify-between text-[10px] mb-1.5">
                                <span className={`font-mono font-medium ${challenge.ratio >= 1 ? "text-pnlPositive" : "text-foreground"}`}>
                                  {challenge.targetAmount !== null
                                    ? `${challenge.profit >= 0 ? "+" : ""}${challenge.profit.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })} / ${challenge.targetAmount.toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                                    : `${Math.min(100, Math.round(challengeRatio * 100))}%`}
                                </span>
                                <span className="text-muted-foreground">{Math.min(100, Math.round(challengeRatio * 100))}%</span>
                              </div>
                              <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                                <div
                                  className={`h-full rounded-full transition-all ${challenge.ratio >= 1 ? "bg-pnlPositive" : "bg-primary"}`}
                                  style={{ width: `${Math.min(100, Math.round(challengeRatio * 100))}%` }}
                                />
                              </div>
                              {!readOnly && challenge.ratio >= 1 && canPromoteInPlace(account) && (
                                <button
                                  className="mt-2 text-[10px] font-semibold text-primary hover:underline"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onPromoteToFunded(account);
                                  }}
                                >
                                  {t.tradingAccounts.promoteToFunded} →
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>

                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
